import { Injectable, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';
import { ToastController, ToastOptions } from '@ionic/angular/lazy';
import { BrowserHandlerService } from './browser-handler.service';

/**
 * PWA Update Service
 * Listens for new app versions when running as installed Web App (PWA).
 * Shows a toast when an update is available and offers to reload.
 */
@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService implements OnDestroy {
  private readonly swUpdate = inject(SwUpdate);
  private readonly toastController = inject(ToastController);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browserHandler = inject(BrowserHandlerService);

  private initialized = false;
  private visibilityChangeListener: (() => void) | null = null;
  private onlineListener: (() => void) | null = null;
  private lastCheckTime = 0;
  private lastHiddenTime = 0;
  private readonly MIN_CHECK_INTERVAL_MS = 30000;
  private readonly LONG_ABSENCE_MS = 30 * 60 * 1000; // Nach 30 Min Abwesenheit: Throttle umgehen

  /**
   * Start listening for updates. Call once from AppComponent.
   * Only runs in browser when Service Worker is enabled (production).
   */
  init(): void {
    if (this.initialized) return;
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.swUpdate.isEnabled) return;

    this.initialized = true;

    // New version ready – show notification and offer reload
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(() => this.notifyUpdateAvailable());

    // Unrecoverable error – suggest reload
    this.swUpdate.unrecoverable.subscribe(() => {
      this.showToast(
        'The app has encountered an error. Please reload the page.',
        true
      );
    });

    // Monitor service worker state and handle "waiting" state (important for iOS PWAs)
    this.monitorServiceWorkerState();

    // Check for updates immediately when service worker is ready
    // Wait for service worker to be ready, then check for updates
    this.swUpdate.isEnabled && this.checkForUpdateWhenReady();

    // Check for updates periodically (every 15 minutes for installed PWAs, 6 hours otherwise)
    this.startPeriodicCheck();

    // Check for updates when app becomes visible (important for installed PWAs)
    this.setupVisibilityChangeListener();

    // Check for updates when device comes back online
    this.setupOnlineListener();
  }

  /**
   * Monitor service worker state and handle "waiting" state.
   * Installed PWAs (iOS, Android, etc.) often have service workers stuck in "waiting" state.
   * This method checks for waiting service workers and activates them automatically.
   * 
   * CRITICAL: For ALL installed PWAs, we MUST call registration.update() to force
   * the service worker to check ngsw.json, because the page doesn't reload when
   * the app comes from background (applies to both iOS and Android).
   */
  private monitorServiceWorkerState(): void {
    if (!('serviceWorker' in navigator)) return;

    const isInstalled = this.browserHandler.isInstalledPWA();

    // Check for waiting service workers periodically
    const checkWaitingWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          // CRITICAL: For ALL installed PWAs (iOS, Android, etc.), force update check
          // This is essential because:
          // 1. Page doesn't reload when app comes from background (iOS & Android)
          // 2. Service worker won't automatically check ngsw.json without explicit request
          // 3. Both iOS and Android cache ngsw.json aggressively
          if (isInstalled) {
            try {
              await registration.update();
              console.debug('[PWA Update] Forced service worker update check (installed PWA)');
            } catch (error) {
              console.debug('[PWA Update] Error forcing update check:', error);
            }
          }
          
          // Check if there's a waiting service worker
          if (registration.waiting) {
            console.log('[PWA Update] Found waiting service worker, activating...');
            // Skip waiting and activate immediately
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            
            // Also try to activate via SwUpdate if available
            if (this.swUpdate.isEnabled) {
              try {
                await this.swUpdate.activateUpdate();
                window.location.reload();
              } catch (error) {
                console.debug('[PWA Update] Could not activate update via SwUpdate:', error);
                // Fallback: reload anyway
                window.location.reload();
              }
            } else {
              // If SwUpdate not available, reload anyway
              window.location.reload();
            }
          }

          // Also check for installing service worker
          if (registration.installing) {
            registration.installing.addEventListener('statechange', () => {
              if (registration.waiting) {
                console.log('[PWA Update] Service worker installed, activating waiting worker...');
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                // For installed PWAs, reload immediately to activate new version
                if (isInstalled) {
                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                }
              }
            });
          }
        }
      } catch (error) {
        console.debug('[PWA Update] Error checking service worker state:', error);
      }
    };

    checkWaitingWorker();
    const intervalMs = isInstalled ? 5 * 60 * 1000 : 10 * 60 * 1000;
    setInterval(checkWaitingWorker, intervalMs);
  }

  /**
   * Check for updates once the service worker is ready.
   * Reduzierte Prüfungen um Überlastung zu vermeiden.
   */
  private checkForUpdateWhenReady(): void {
    const isInstalled = this.browserHandler.isInstalledPWA();
    this.checkForUpdate();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then(() => this.checkForUpdate())
        .catch(() => {
          setTimeout(() => this.swUpdate.isEnabled && this.checkForUpdate(), 5000);
        });
    }

    if (isInstalled) {
      window.addEventListener('focus', () => {
        setTimeout(() => this.checkForUpdate(), 500);
      }, { once: false });
    }
  }

  /**
   * Check the server for a new version once.
   * @param force – Throttle umgehen (z.B. bei manueller "Update prüfen"-Aktion)
   */
  checkForUpdate(force = false): void {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) return;
    if (!force && Date.now() - this.lastCheckTime < this.MIN_CHECK_INTERVAL_MS) return;
    this.lastCheckTime = Date.now();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration()
        .then(async (registration) => {
          if (!registration) return;
          if (registration.waiting) {
            console.log('[PWA Update] Found waiting service worker, activating...');
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            setTimeout(() => this.activateAndReload(), 1000);
            return;
          }
          try {
            await registration.update();
            console.debug('[PWA Update] registration.update() completed');
          } catch (error) {
            console.debug('[PWA Update] registration.update() failed:', error);
          }
        })
        .catch((error) => console.debug('[PWA Update] getRegistration failed:', error));
    }

    this.swUpdate.checkForUpdate()
      .then(() => {
        console.debug('[PWA Update] checkForUpdate completed');
        setTimeout(() => {
          navigator.serviceWorker?.getRegistration?.()?.then((reg) => {
            if (reg?.waiting) {
              reg.waiting.postMessage({ type: 'SKIP_WAITING' });
              setTimeout(() => this.activateAndReload(), 1000);
            }
          });
        }, 2000);
      })
      .catch((error) => console.debug('[PWA Update] checkForUpdate failed:', error));
  }

  /**
   * Manueller Update-Check (z.B. "Update prüfen" im Version-Modal).
   * Umgeht Throttle, führt einen gezielten Check durch.
   */
  async forceUpdateCheck(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) return;
    this.lastCheckTime = 0;
    console.debug('[PWA Update] Force update check (manual)');

    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            setTimeout(() => this.activateAndReload(), 1000);
            return;
          }
          await registration.update();
          if (registration.active) {
            registration.active.postMessage({ action: 'CHECK_FOR_UPDATES', nonce: Date.now() });
          }
        }
      } catch (error) {
        console.debug('[PWA Update] forceUpdateCheck registration failed:', error);
      }
    }

    try {
      await this.swUpdate.checkForUpdate();
      setTimeout(() => {
        navigator.serviceWorker?.getRegistration?.()?.then((reg) => {
          if (reg?.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            setTimeout(() => this.activateAndReload(), 1000);
          }
        });
      }, 2000);
    } catch (error) {
      console.debug('[PWA Update] forceUpdateCheck failed:', error);
    }
  }

  /**
   * Check for updates and activate immediately without showing toast.
   * Used after PWA installation to ensure the latest version is used.
   * This method listens for VERSION_READY events and activates them immediately.
   */
  checkAndActivateUpdateImmediately(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) return;

    this.checkForUpdate(true);

    // Listen for VERSION_READY events and activate immediately
    // This subscription will only be active for a short time (one-time check)
    const subscription = this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(async () => {
        // Update is ready - activate immediately without showing toast
        subscription.unsubscribe(); // Unsubscribe after first update
        await this.activateAndReload();
      });

    // Cleanup subscription after 10 seconds if no update is found
    setTimeout(() => {
      subscription.unsubscribe();
    }, 10000);
  }

  private notifyUpdateAvailable(): void {
    // VERSION_READY means a new version is available and ready to activate
    // Show notification immediately - user can choose to update now or later
    this.showToast('New version available. Update now?', true);
  }

  private async showToast(
    message: string,
    withReloadButton: boolean
  ): Promise<void> {
    const buttons: ToastOptions['buttons'] = withReloadButton
      ? [
          { text: 'Later', role: 'cancel' },
          {
            text: 'Update now',
            role: 'confirm',
            handler: () => this.activateAndReload()
          }
        ]
      : [{ text: 'OK', role: 'cancel' }];

    const toast = await this.toastController.create({
      message,
      duration: withReloadButton ? 0 : 4000,
      position: 'bottom',
      mode: 'ios',
      color: 'primary',
      swipeGesture: 'vertical',
      buttons
    });
    await toast.present();
  }

  private async activateAndReload(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
      window.location.reload();
    } catch {
      window.location.reload();
    }
  }

  private startPeriodicCheck(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const isIOS = this.browserHandler.isIOS();
    const isInstalled = this.browserHandler.isInstalledPWA();
    
    // More frequent checks for installed PWAs (iOS, Android, etc.)
    // Both iOS and Android require frequent checks because:
    // 1. Page doesn't reload when app comes from background
    // 2. Service worker must be explicitly told to check for updates
    // 3. Aggressive caching on mobile devices
    let intervalMs: number;
    if (isIOS && isInstalled) {
      intervalMs = 5 * 60 * 1000;  // 5 minutes for iOS installed PWAs (most aggressive caching)
    } else if (isInstalled) {
      intervalMs = 10 * 60 * 1000;  // 10 minutes for Android and other installed PWAs
    } else {
      intervalMs = 6 * 60 * 60 * 1000; // 6 hours for browser tabs
    }
    
    setInterval(() => {
      // Only check if service worker is still enabled
      if (this.swUpdate.isEnabled) {
        // CRITICAL: For ALL installed PWAs (iOS, Android, etc.), we MUST call registration.update()
        // to force the service worker to check ngsw.json, because:
        // 1. The page doesn't reload when app comes from background (iOS & Android)
        // 2. The service worker won't automatically check ngsw.json without explicit request
        // 3. Both iOS and Android cache ngsw.json aggressively
        if (isInstalled && 'serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration()
            .then((registration) => {
              if (registration) {
                // Force service worker to check for updates by calling update()
                // This tells the service worker to fetch ngsw.json and compare versions
                // Works for both iOS and Android installed PWAs
                registration.update().catch((error) => {
                  console.debug('[PWA Update] Error forcing periodic update check:', error);
                  // Continue with normal check even if update() fails
                });
              }
            })
            .catch(() => {
              // Continue with normal check
            });
        }
        
        this.checkForUpdate();
      }
    }, intervalMs);
  }

  /**
   * Check for updates when the app becomes visible again.
   * Nach längerer Abwesenheit (>30 Min) Throttle umgehen, damit Updates zuverlässig erkannt werden.
   *
   * WICHTIG (bfcache): Wenn event.persisted === true, wurde die Seite aus dem Back-Forward-Cache
   * wiederhergestellt – ein eingefrorenes Snapshot. Das kann eine veraltete Version zeigen.
   * Wir laden die Seite neu, damit immer eine frische Version vom Service Worker geladen wird.
   */
  private setupVisibilityChangeListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const isInstalled = this.browserHandler.isInstalledPWA();

    this.visibilityChangeListener = () => {
      if (document.hidden) {
        this.lastHiddenTime = Date.now();
      } else {
        const absenceDuration = Date.now() - this.lastHiddenTime;
        const forceCheck = absenceDuration > this.LONG_ABSENCE_MS;
        setTimeout(() => {
          if (this.swUpdate.isEnabled) {
            this.checkForUpdate(forceCheck);
          }
        }, 1000);
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeListener);

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        // bfcache-Restore: Eingefrorenes Snapshot kann alte Version zeigen → Neu laden
        window.location.reload();
        return;
      }
      if (isInstalled) {
        const forceCheck = Date.now() - this.lastHiddenTime > this.LONG_ABSENCE_MS;
        setTimeout(() => {
          if (this.swUpdate.isEnabled) {
            this.checkForUpdate(forceCheck);
          }
        }, 1500);
      }
    });
  }

  /**
   * Check for updates when the device comes back online.
   * This ensures updates are detected even if the device was offline.
   */
  private setupOnlineListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    this.onlineListener = () => {
      // Check for updates when device comes back online
      // Small delay to ensure network is stable
      setTimeout(() => {
        this.checkForUpdate();
      }, 2000);
    };
    
    window.addEventListener('online', this.onlineListener);
  }

  /**
   * Service Worker deregistrieren (falls vorhanden) und Seite neu laden.
   * Kritisch: Vor dem Reload alle Caches leeren – der ngsw liefert index.html
   * aus dem Cache, Query-Parameter (_t=…) ändern den Cache-Key oft nicht.
   * Ohne Cache-Leerung würde die alte Version weiterhin angezeigt.
   */
  async unregisterAndReload(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.unregister();
          console.debug('[PWA Update] Service Worker deregistriert');
        }
      }

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
        console.debug('[PWA Update] Caches geleert');
      }
    } catch (error) {
      console.debug('[PWA Update] Fehler beim Aufräumen:', error);
    }

    const separator = window.location.search ? '&' : '?';
    const hash = window.location.hash || '';
    const url = window.location.origin + window.location.pathname +
      window.location.search + separator + '_t=' + Date.now() + hash;
    window.location.replace(url);
  }

  /**
   * Cleanup listeners when service is destroyed (if needed)
   */
  ngOnDestroy(): void {
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
    }
  }
}
